import { useEffect, useRef, useState } from 'react';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { useTranslation } from 'react-i18next';
import { CloseButton } from '@components/close-button';
import { Loader } from '@components/loader';
import { ModalStickyFooter } from '@components/modals/modal-sticky-footer';
import { MD_VIEWPORT, SM_VIEWPORT } from '@constants/viewport';
import { useWindowSize } from '@hooks/use-window-size';
import { rgbHex } from '@utils/rgb-to-hex/rgb-hex';

import { Props } from './color-picker-modal.types';

export const ColorPickerModal = ({ handleClose, handleSubmit, imgSrc, defaultColor, isEditDisabled }: Props) => {
  const { t } = useTranslation();
  const initialColor = defaultColor || '#ffffff';
  const [currentColor, setCurrentColor] = useState(initialColor);
  const [selectedColor, setSelectedColor] = useState(initialColor);
  const [isCanvasLoading, setIsCanvasLoading] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isMobileViewPort = useWindowSize(SM_VIEWPORT as number);
  const isTabletViewPort = useWindowSize(MD_VIEWPORT as number);

  const getColorMouseMove = (evt: MouseEvent) => {
    const canvasObj = canvasRef.current;
    const context = canvasObj?.getContext('2d');

    if (context) {
      const { data } = context.getImageData(evt.offsetX, evt.offsetY, 1, 1);
      const hex = rgbHex(data[0], data[1], data[2]);
      setCurrentColor(`#${hex}`);
    }
  };

  const getColorClick = () => setSelectedColor(`${currentColor}`);

  useEffect(() => {
    const canvasObj = canvasRef.current;

    const image = new Image();

    if (canvasObj && image) {
      canvasObj.addEventListener('mousemove', getColorMouseMove);
      const context = canvasObj.getContext('2d');

      image.onload = () => {
        setIsCanvasLoading(false);
        canvasObj.width = image.width;
        canvasObj.height = image.height;
        context?.drawImage(image, 0, 0);
      };

      image.crossOrigin = 'anonymous';
      image.src = `${imgSrc}${isMobileViewPort ? '?w=300' : isTabletViewPort ? '?w=500' : '?w=512'}`;

      image.onerror = () => {
        setIsCanvasLoading(false);
      };
    }
  }, [imgSrc, isMobileViewPort, isTabletViewPort]);

  return (
    <>
      <div className='flex flex-row justify-between items-center bg-primary px-4 md:px-10 py-6'>
        <div className='flex flex-row items-center'>
          <p className='text-left text-primary text-headline-m'>{t('offers:selectColorModalTitle')}</p>
        </div>

        <CloseButton handleClose={handleClose} />
      </div>

      <div className='flex flex-col md:flex-row gap-3 items-center justify-around py-5'>
        {imgSrc && <canvas ref={canvasRef} width={0} height={0} className='cursor-dropper' onClick={getColorClick} />}
        {imgSrc && isCanvasLoading && <Loader />}
        <HexColorPicker
          className={'w-[90%]!'}
          color={selectedColor}
          onChange={(e) => !isEditDisabled && setSelectedColor(e)}
        />
      </div>

      <div className='flex gap-2 items-center justify-center pb-5'>
        <span className='text-primary'>{t('offers:selectColorModalLabel')}:</span>
        <div
          style={{
            backgroundColor: selectedColor,
            width: '25px',
            height: '25px',
          }}></div>
        <HexColorInput
          color={selectedColor}
          disabled={isEditDisabled}
          onChange={setSelectedColor}
          className='bg-primary text-primary rounded-md'
        />
      </div>

      <ModalStickyFooter
        primaryButtonText={t('common:save')}
        onPrimaryButtonClick={() => handleSubmit(selectedColor)}
        isSubmitDisabled={isEditDisabled}
        secondaryButtonText={t('common:deleteColor')}
        onSecondaryButtonClick={() => handleSubmit('')}
        isSecondaryButtonDisabled={isEditDisabled}
      />
    </>
  );
};
